import Button from '@atoms/Button/Button';
import { IArticle } from '@models/IArticle';
import { ILink } from '@models/ILink';
import { IListingComponent } from '@models/IListingComponent';
import ArticleLayoutsEnum from '@models/enums/ArticleLayoutsEnum';
import PostTypes from '@models/enums/PostTypes';
import ListingResultsContainer from '@molecules/ListingResultsContainer/ListingResultsContainer';
import LoadMoreListing from '@molecules/LoadMoreListing/LoadMoreListing';
import ContentFilters from '@organisms/ContentFilters/ContentFilters';
import ListingCardBlock from '@organisms/ListingCardBlock/ListingCardBlock';
import useListingComponentProps from '@utils/hooks/useListingComponentProps';
import classNames from 'classnames';

interface Props extends IListingComponent {
  readonly articleType: string;
  readonly authorTypes: string[];
  readonly authorPageId: string;
  readonly campaign: string[];
  readonly hideLoadMore?: boolean;
  readonly listingLayout: ArticleLayoutsEnum;
  readonly endpoint: string;
  readonly sectionTitle?: string;
  readonly showMeEverythingLink?: ILink;
  readonly specialism: string[];
  readonly targetSegment: string[];
  readonly type: string[];
  readonly showExpandedFilters?: boolean;
}

const ArticleTypeListing = (props: Props) => {
  const {
    anchorId = '',
    authorPageId = '',
    articleType = '',
    authorTypes = [],
    listingLayout,
    sectionTitle,
    showFacets,
    showMeEverythingLink,
    targetSegment,
    hideLoadMore = false,
    facetsToShow,
    viewedLabel,
    showExpandedFilters,
  } = props;

  const preFilterParams = {
    ArticleType: [articleType],
    AuthorType: authorTypes,
    targetSegmentList: targetSegment,
  };

  const persistingParams = { AuthorPageId: [authorPageId] };

  const layoutProperties = {
    [ArticleLayoutsEnum.ONE]: { pageSize: 1, reducedWidth: true },
    [ArticleLayoutsEnum.ONE_TWO]: { pageSize: 3, reducedWidth: true },
    [ArticleLayoutsEnum.TWO]: { pageSize: 2, reducedWidth: true },
    [ArticleLayoutsEnum.TWO_TWO]: { pageSize: 4, reducedWidth: true },
    [ArticleLayoutsEnum.THREE]: { pageSize: 12, reducedWidth: false },
    [ArticleLayoutsEnum.THREE_TWO_THREE]: { pageSize: 10, reducedWidth: false },
    [ArticleLayoutsEnum.THREE_TWO_TWO]: { pageSize: 7, reducedWidth: false },
  };

  const { results = [], ...listingProps } = useListingComponentProps({
    ...props,
    facetsToShow: facetsToShow.concat(['targetSegmentList']),
    preFilterParams,
    persistingParams,
    pageSize: layoutProperties[listingLayout].pageSize,
  });

  return (
    <section
      className="component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      id={`a-${anchorId}`}
    >
      <div
        className="container xl:grid xl:grid-cols-12"
        data-testid="CT-23/47-Article-Type-Listing"
      >
        <div
          className={classNames(
            layoutProperties[listingLayout].reducedWidth
              ? 'xl:col-span-10 xl:col-start-2'
              : 'xl:col-span-full'
          )}
        >
          <div className="flex flex-col items-baseline justify-between space-y-6 pb-10 xl:flex-row xl:space-x-6">
            {sectionTitle && (
              <h2
                className="shrink-0"
                data-testid="CT-23/47-Article-Type-Listing-Title"
              >
                {sectionTitle}
              </h2>
            )}
            <div className="flex w-full shrink-0 flex-wrap justify-between gap-y-6 xl:ml-auto xl:w-auto xl:space-x-6">
              {showFacets && (
                <ContentFilters
                  {...listingProps}
                  showExpandedFilters={showExpandedFilters}
                />
              )}
              {showMeEverythingLink?.name && showMeEverythingLink.url && (
                <Button
                  link={showMeEverythingLink}
                  className={classNames('shrink-0', {
                    'hidden xl:block': hideLoadMore,
                  })}
                  dataTestId="CT-23/47-Article-Type-Listing-Show-Me-Everything-CTA"
                />
              )}
            </div>
          </div>
          <div className="space-y-28">
            <ListingResultsContainer
              results={results}
              {...listingProps}
              resultsClassName="space-y-24 lg:space-y-32"
            >
              <ListingCardBlock
                results={results as IArticle[]}
                type={PostTypes.ARTICLE}
                layoutType={listingLayout}
                {...listingProps}
              />
            </ListingResultsContainer>

            {!hideLoadMore && (
              <LoadMoreListing
                results={results as IArticle[]}
                {...listingProps}
                alignLeft={layoutProperties[listingLayout].reducedWidth}
                viewedLabel={
                  layoutProperties[listingLayout].reducedWidth
                    ? ''
                    : viewedLabel
                }
              />
            )}
          </div>
          {showMeEverythingLink?.name &&
            showMeEverythingLink.url &&
            hideLoadMore && (
              <div className="mt-24 flex justify-center xl:hidden">
                <Button
                  link={showMeEverythingLink}
                  dataTestId="CT-23/47-Article-Type-Listing-Show-Me-Everything-CTA"
                />
              </div>
            )}
        </div>
      </div>
    </section>
  );
};

export default ArticleTypeListing;
